import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  usePublicationInbox,
} from '@/hooks/usePublicationInbox';
import {
  usePublicationInboxDetail,
} from '@/hooks/usePublicationInboxDetail';

import type {
  PublicationInboxFolder,
} from '@/lib/publicationInboxPresentation';

import PublicationAttentionSummary from './PublicationAttentionSummary';
import PublicationDetailPanel from './PublicationDetailPanel';
import PublicationInboxFilters, {
  type PublicationInboxFilterState,
} from './PublicationInboxFilters';
import PublicationInboxFolders from './PublicationInboxFolders';
import PublicationInboxList from './PublicationInboxList';
import PublicationInboxDecisionPanel from './PublicationInboxDecisionPanel';
import PublicationInboxLinkPanel from './PublicationInboxLinkPanel';

export default function PublicationInboxWorkspace() {
  const [folder, setFolder] =
    useState<PublicationInboxFolder>(
      'today'
    );
  const [filters, setFilters] =
    useState<PublicationInboxFilterState>({
      search: '',
      tribunal: '',
      source: '',
      attention: '',
      dateFrom: '',
      dateTo: '',
    });
  const [debouncedSearch, setDebouncedSearch] =
    useState('');
  const [selectedId, setSelectedId] =
    useState<string | null>(null);
  const [cursor, setCursor] =
    useState('0');

  useEffect(() => {
    const timeout =
      globalThis.setTimeout(
        () =>
          setDebouncedSearch(
            filters.search.trim()
          ),
        250
      );

    return () =>
      globalThis.clearTimeout(timeout);
  }, [filters.search]);

  const query = useMemo(
    () => ({
      folder,
      search: debouncedSearch,
      tribunal: filters.tribunal.trim(),
      source: filters.source.trim(),
      attention: filters.attention,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      limit: 5,
      cursor,
    }),
    [
      folder,
      debouncedSearch,
      filters.tribunal,
      filters.source,
      filters.attention,
      filters.dateFrom,
      filters.dateTo,
      cursor,
    ]
  );

  const {
    items,
    summary,
    pagination,
    generatedAt,
    isLoading,
    error,
    refresh,
  } = usePublicationInbox(query);

  useEffect(() => {
    if (
      selectedId &&
      items.some(
        (item) => item.id === selectedId
      )
    ) {
      return;
    }

    setSelectedId(items[0]?.id ?? null);
  }, [items, selectedId]);

  const selectedItem =
    items.find(
      (item) => item.id === selectedId
    ) ?? null;

  const {
    detail,
    isLoading: isLoadingDetail,
    error: detailError,
    refresh: refreshDetail,
  } = usePublicationInboxDetail(
    selectedItem?.id ?? null
  );

  const handleRefresh = async () => {
    await refresh();
    await refreshDetail();
  };

  const handleFolderChange = (
    next: PublicationInboxFolder
  ) => {
    setFolder(next);
    setCursor('0');
    setSelectedId(null);
  };

  const handleFiltersChange = (
    next: PublicationInboxFilterState
  ) => {
    setFilters(next);
    setCursor('0');
  };

  const currentOffset =
    Number.parseInt(
      pagination.cursor,
      10
    ) || 0;

  const firstVisible =
    pagination.total > 0
      ? currentOffset + 1
      : 0;

  const lastVisible =
    currentOffset + items.length;

  const previousCursor =
    String(
      Math.max(
        0,
        currentOffset - pagination.limit
      )
    );

  return (
    <div className="space-y-4">
      <PublicationAttentionSummary
        summary={summary}
        onSelectFolder={
          handleFolderChange
        }
      />

      <PublicationInboxFilters
        value={filters}
        onChange={handleFiltersChange}
      />

      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <div className="text-xs text-foreground-500">
          {pagination.total} registro
          {pagination.total === 1
            ? ''
            : 's'}{' '}
          nesta caixa
          {pagination.truncated
            ? ' • leitura limitada ao lote operacional'
            : ''}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-foreground-400">
            {firstVisible}–{lastVisible} de {pagination.total}
          </span>

          <button
            type="button"
            onClick={() =>
              setCursor(previousCursor)
            }
            disabled={
              isLoading ||
              currentOffset <= 0
            }
            className="rounded-lg border border-background-200 px-3 py-1.5 text-xs font-medium text-foreground-600 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Anterior
          </button>

          <button
            type="button"
            onClick={() => {
              if (pagination.nextCursor) {
                setCursor(
                  pagination.nextCursor
                );
              }
            }}
            disabled={
              isLoading ||
              !pagination.nextCursor
            }
            className="rounded-lg border border-background-200 px-3 py-1.5 text-xs font-medium text-foreground-600 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Próxima
          </button>

        <button
          type="button"
          onClick={() =>
            void handleRefresh()
          }
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-lg border border-background-200 px-3 py-1.5 text-xs font-medium text-foreground-600 hover:bg-background-100 disabled:opacity-50"
        >
          <i
            className={`ri-refresh-line ${
              isLoading
                ? 'animate-spin'
                : ''
            }`}
          ></i>
          Atualizar
        </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="xl:col-span-2">
          <PublicationInboxFolders
            activeFolder={folder}
            summary={summary}
            onChange={handleFolderChange}
          />
        </div>

        <div className="min-w-0 xl:col-span-4">
          <PublicationInboxList
            items={items}
            selectedId={selectedId}
            isLoading={isLoading}
            onSelect={setSelectedId}
          />
        </div>

        <div className="min-w-0 space-y-4 xl:col-span-6">
          <PublicationInboxLinkPanel
            item={selectedItem}
            onChanged={handleRefresh}
          />

          <PublicationInboxDecisionPanel
            item={selectedItem}
            detail={detail}
            onChanged={handleRefresh}
          />

          <PublicationDetailPanel
            item={selectedItem}
            detail={detail}
            isLoading={isLoadingDetail}
            error={detailError}
          />
        </div>
      </div>

      {generatedAt && (
        <p className="px-1 text-[10px] text-foreground-400">
          Read model atualizado em{' '}
          {new Intl.DateTimeFormat(
            'pt-BR',
            {
              dateStyle: 'short',
              timeStyle: 'medium',
              timeZone:
                'America/Sao_Paulo',
            }
          ).format(new Date(generatedAt))}
          .
        </p>
      )}
    </div>
  );
}